'use server'

import { apiRequest, ApiResponse } from '../request'
import type { FinancialPeriod } from '../financial/financial-dashboard-api'

const BASE = 'marketing/dashboard'

export type MarketingComparisonMode = 'previous' | 'year' | 'custom' | 'none'
export type MarketingDashboardScope = 'company' | 'organization'
export type MarketingOrigin = 'paid' | 'organic' | 'all'
export type MarketingGranularity = 'hour' | 'day' | 'week' | 'month'

export type MarketingChannel =
    | 'META_ADS'
    | 'GOOGLE_ADS'
    | 'TIKTOK_ADS'
    | 'CAMPAIGN'
    | 'WHATSAPP'
    | 'INSTAGRAM'
    | 'MESSENGER'
    | 'WEBSITE'
    | 'REFERRAL'
    | 'MANUAL'
    | 'OTHER'
    | 'APPOINTMENT'
    | 'NO_LEAD'

export interface MarketingSummary {
    leads: number
    leadsCreatedAtSale: number
    paidLeads: number
    leadsWithDeal: number
    wonLeads: number
    wonValue: number
    salesCount: number
    salesValue: number
    attributedSalesValue: number
    paidSalesValue: number
    buyers: number
    averageTicket: number | null
    leadToSaleRate: number | null
    newChats: number
    contacts: number
    answered: number
    unanswered: number
    medianFirstResponseSeconds: number | null
    answeredWithin5mRate: number | null
    aiAnsweredRate: number | null
    campaignClicks: number
    adSpend: number | null
    adImpressions: number | null
    adClicks: number | null
    platformResults: number | null
    costPerLead: number | null
    costPerBuyer: number | null
    roas: number | null
    paidBuyers: number
}

export interface MarketingChannelRow {
    channel: MarketingChannel
    leads: number
    leadsWithDeal: number
    wonLeads: number
    wonValue: number
    salesCount: number
    salesValue: number
    buyers: number
}

export interface MarketingSeriesPoint {
    key: string
    leads: number
    paidLeads: number
    salesValue: number
    newChats: number
    spend: number
}

export interface MarketingFunnel {
    leads: number
    withDeal: number
    won: number
    bought: number
}

export interface MarketingChatChannelRow {
    channel: string
    newChats: number
    contacts: number
    answered: number
    answeredByAi: number
    medianFirstResponseSeconds: number | null
    medianHumanSeconds: number | null
    medianAiSeconds: number | null
    answeredWithin5m: number
}

export interface MarketingCampaignRow {
    id: number
    name: string
    status: string
    budget: number | null
    clicks: number
    leads: number
    salesCount: number
    salesValue: number
}

export interface MarketingAdStats {
    spend: number
    impressions: number
    clicks: number
    platformLeads: number
    platformConversations: number
    leads: number
    wonLeads: number
    salesCount: number
    salesValue: number
}

export interface MarketingAdRow extends MarketingAdStats {
    adId: string | null
    adName: string | null
    adsetName: string | null
}

export interface MarketingAdCampaignRow extends MarketingAdStats {
    campaignId: string | null
    campaignName: string | null
    clickToWhatsappLeads: number
    formLeads: number
    ads: MarketingAdRow[]
}

export interface MarketingSnapshot {
    summary: MarketingSummary
    channels: MarketingChannelRow[]
    series: MarketingSeriesPoint[]
    funnel: MarketingFunnel
    chatChannels: MarketingChatChannelRow[]
    heatmap: Array<{ dow: number; hour: number; total: number }>
    campaigns: MarketingCampaignRow[]
    metaAds: MarketingAdCampaignRow[]
    googleAds: MarketingAdCampaignRow[]
    adAccounts: Array<{
        platform: string
        accountId: string
        lastSuccessAt: string | null
        lastError: string | null
        backfilled: boolean
    }>
    adCurrencies: string[]
    truncated: boolean
}

export interface MarketingDashboardResponse {
    range: { startDay: string; endDay: string }
    compareRange: { startDay: string; endDay: string } | null
    compareMode: MarketingComparisonMode
    granularity: MarketingGranularity
    scope: MarketingDashboardScope
    origin: MarketingOrigin
    current: MarketingSnapshot
    previous: MarketingSnapshot | null
    lastUpdated: string
}

export interface MarketingDashboardParams {
    period: FinancialPeriod
    startDate?: string
    endDate?: string
    compare: MarketingComparisonMode
    compareStartDate?: string
    compareEndDate?: string
    scope: MarketingDashboardScope
    origin: MarketingOrigin
}

export async function getMarketingDashboard(
    params: MarketingDashboardParams,
): Promise<ApiResponse<MarketingDashboardResponse>> {
    const query = new URLSearchParams()
    query.set('period', params.period)
    if (params.startDate) query.set('startDate', params.startDate)
    if (params.endDate) query.set('endDate', params.endDate)
    query.set('compare', params.compare)
    if (params.compareStartDate) query.set('compareStartDate', params.compareStartDate)
    if (params.compareEndDate) query.set('compareEndDate', params.compareEndDate)
    query.set('scope', params.scope)
    query.set('origin', params.origin)
    return apiRequest<MarketingDashboardResponse>(`${BASE}?${query.toString()}`, {})
}

export interface AdMetricsSyncResult {
    accounts: number
    rows: number
    errors: Array<{ organizationId: number; adAccountId: string; message: string }>
}

export async function syncAdMetrics(): Promise<ApiResponse<AdMetricsSyncResult>> {
    return apiRequest<AdMetricsSyncResult>(`${BASE}/ad-metrics/sync`, {}, 'post')
}

export const MARKETING_CHANNEL_LABELS: Record<MarketingChannel, string> = {
    META_ADS: 'Meta Ads',
    GOOGLE_ADS: 'Google Ads',
    TIKTOK_ADS: 'TikTok Ads',
    CAMPAIGN: 'Campanha própria',
    WHATSAPP: 'WhatsApp orgânico',
    INSTAGRAM: 'Instagram orgânico',
    MESSENGER: 'Messenger',
    WEBSITE: 'Site',
    REFERRAL: 'Indicação',
    MANUAL: 'Cadastro manual',
    OTHER: 'Outros',
    APPOINTMENT: 'Criado no agendamento',
    NO_LEAD: 'Sem lead (cliente direto)',
}

export const MARKETING_PAID_CHANNELS: ReadonlySet<MarketingChannel> = new Set([
    'META_ADS',
    'GOOGLE_ADS',
    'TIKTOK_ADS',
])

export const MARKETING_COMPARISON_LABELS: Record<MarketingComparisonMode, string> = {
    previous: 'Período anterior',
    year: 'Mesmo período do ano anterior',
    custom: 'Intervalo personalizado',
    none: 'Sem comparação',
}

export const MARKETING_ORIGIN_LABELS: Record<MarketingOrigin, string> = {
    paid: 'Tráfego pago',
    organic: 'Orgânico',
    all: 'Todos',
}

export const CHAT_CHANNEL_LABELS: Record<string, string> = {
    WHATSAPP: 'WhatsApp',
    INSTAGRAM: 'Instagram',
    MESSENGER: 'Messenger',
    DEX_TALK: 'Dex',
}
